import { z } from 'zod';
import { currencySchema, divisionSchema, kpiSourceSchema, pillarAccessSchema, pillarSchema, targetDirectionSchema, unitSchema, valueTypeSchema, } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { formulaNodeSchema } from '../../kpis/schemas/kpis.schemas.js';
import { isoDateSchema, weekSchema } from '../../weeks/schemas/weeks.schemas.js';
export const TRACKER_TEXT_MAX_LENGTH = 500;
export const TRACKER_NOTE_MAX_LENGTH = 2000;
/** One save may touch every cell of a 60-KPI × 104-week grid, with room to spare. */
export const TRACKER_MAX_EDITS = 10_000;
/** A custom From→To range (at most 104 weeks, never reversed) or the explicit "All weeks". */
const customRangeSchema = z.object({
    fromWeekId: uuidSchema,
    toWeekId: uuidSchema,
    pillar: pillarSchema.optional(),
});
const allWeeksSchema = z.object({
    all: z.literal(true),
    pillar: pillarSchema.optional(),
});
export const trackerGridInputSchema = z.union([customRangeSchema, allWeeksSchema]);
export const trackerPillarSchema = z.object({
    pillar: pillarSchema,
    access: pillarAccessSchema,
});
export const trackerKpiSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    pillar: pillarSchema,
    division: divisionSchema.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    measurementMethod: z.string().nullable(),
    unit: unitSchema,
    currency: currencySchema.nullable(),
    valueType: valueTypeSchema,
    source: kpiSourceSchema,
    dataSource: z.string().nullable(),
    /** Same-pillar parent; display only. */
    parentKpiId: uuidSchema.nullable(),
    /** 0 = top level; the server orders parents before their sub-KPIs. */
    depth: z.number().int().min(0),
    displayOrder: z.number().int(),
    /** The caller has EDIT on the pillar and the KPI is manual. */
    editable: z.boolean(),
    /** A calculated KPI whose sources reach into a pillar the caller cannot see: blank everywhere, no formula. */
    restricted: z.boolean(),
    /** The formula with KPI names, `Vehicles sold ÷ Leads generated × 100`; `null` for a manual or restricted KPI. */
    formulaPreview: z.string().nullable(),
});
/** One stored cell. A numeric KPI carries `value`; a Text KPI carries `textValue`. */
export const trackerValueSchema = z.object({
    value: z.number().nullable(),
    textValue: z.string().nullable(),
    comment: z.string().nullable(),
    reason: z.string().nullable(),
    followUp: z.string().nullable(),
    updatedAt: z.string().datetime(),
});
/**
 * A calculated KPI's validated formula — one for every active, non-restricted calculated KPI the shown KPIs need,
 * nested ones included — so the browser recomputes calculated cells over staged edits with the engine. Restricted
 * KPIs never appear here.
 */
export const trackerCalcDefSchema = z.object({
    kpiId: uuidSchema,
    formula: formulaNodeSchema,
});
/** The active target applying to a KPI at the range's To week, with its resolved week bounds. */
export const trackerTargetSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    value: z.number(),
    unit: unitSchema,
    currency: currencySchema.nullable(),
    direction: targetDirectionSchema,
    fromWeekDate: isoDateSchema,
    toWeekDate: isoDateSchema,
});
export const trackerGridOutputSchema = z.object({
    /** The caller's visible pillars, in pillar order. */
    pillars: z.array(trackerPillarSchema),
    /** Active KPIs of the visible pillars: pillar order, parents before sub-KPIs, then display order. */
    kpis: z.array(trackerKpiSchema),
    /** The weeks of the range, oldest first. */
    weeks: z.array(weekSchema),
    /** The week before the range (for the first column's week-over-week movement); its values are in `values`. */
    previousWeek: weekSchema.nullable(),
    /**
     * Keyed `${kpiId}:${weekId}`; a missing key is a blank cell. Holds the manual cells of the shown KPIs and of the
     * active manual KPIs their formulas read (which may sit outside the pillar filter); never a calculated KPI's
     * dormant manual rows, never a restricted or inactive KPI's cells.
     */
    values: z.record(z.string(), trackerValueSchema),
    calcDefs: z.array(trackerCalcDefSchema),
    /** Keyed by KPI id: the active target covering the To week (at most one), when there is one. */
    targets: z.record(z.string(), z.array(trackerTargetSchema)),
    /** The week of the Sunday on or before today in Asia/Amman; `null` when it was never generated. */
    currentWeekId: uuidSchema.nullable(),
});
const noteSchema = z.string().max(TRACKER_NOTE_MAX_LENGTH, `Use at most ${TRACKER_NOTE_MAX_LENGTH} characters`).nullable().optional();
/**
 * One cell to write. `value` is the whole new value: a number (or numeric text such as `1,250`) for a numeric
 * KPI, text for a Text KPI, `null` or blank to clear — and clearing deletes the row with its notes. Omitted notes
 * keep what is stored; `null` or blank removes one.
 */
export const trackerEditSchema = z.object({
    kpiId: uuidSchema,
    weekId: uuidSchema,
    value: z.union([z.number().finite(), z.string().max(TRACKER_TEXT_MAX_LENGTH, `Use at most ${TRACKER_TEXT_MAX_LENGTH} characters`), z.null()]),
    comment: noteSchema,
    reason: noteSchema,
    followUp: noteSchema,
});
export const saveTrackerValuesInputSchema = z.object({
    edits: z.array(trackerEditSchema).min(1, 'Nothing to save').max(TRACKER_MAX_EDITS, `Save at most ${TRACKER_MAX_EDITS} cells at once`),
});
export const saveTrackerValuesOutputSchema = z.object({
    /** Rows inserted or updated. */
    saved: z.number().int().min(0),
    /** Rows deleted because their value was cleared. */
    cleared: z.number().int().min(0),
    /** The stored cells after the save (rounded as stored), keyed like `values`; cleared cells are absent. */
    values: z.record(z.string(), trackerValueSchema),
});
//# sourceMappingURL=tracker.schemas.js.map